import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import Select from 'react-select';

import createStore from './store';

import { Spinner } from '../../components/spinner';
import { calendarApi } from '../../features/calendar';
import { FilterEventEnum } from '../../models/filter';
import { Pagination } from '../../components/pagination';
import { DateUtility, DayJSDateStringFormat } from '../../utilities/date';
import { ITableColumn, ITableConfig, Table } from '../../components/table';
import { ICalendarEvent } from '../../models/event';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { ApiUtility } from '../../utilities/api';
import { Alert, AlertType } from '../../components/alert';
import { UmbracoUtility } from '../../utilities/umbraco';
import { ErrorBoundary } from '../../components/error-boundary';

const convertEventDateTimeToStr = (date: string, time: string): string => {
    const dateStr = date.replace('Z', '');
    let dateObj = DateUtility.toDayJS(dateStr);
    if (time) {
        dateObj = DateUtility.copyTime(dateObj, DateUtility.toDateByFormat(time, DayJSDateStringFormat.HHMM));
        return DateUtility.toFormattedString(dateObj, DayJSDateStringFormat.YYYYMMDDHHMM);
    }

    return DateUtility.toFormattedString(dateObj, DayJSDateStringFormat.YYYYMMDD);
};

const getColumnConfig = (filterType: FilterEventEnum): Array<ITableColumn<ICalendarEvent>> => [
    {
        key: 'date',
        header: 'Event Date',
        cell: (rowData: ICalendarEvent) => {
            const { eventInformation } = rowData;
            if (!eventInformation) {
                return null;
            }
            const { startDate, startTime, endDate, endTime, timezoneInput } = eventInformation;
            const start = convertEventDateTimeToStr(startDate, startTime);
            const end = convertEventDateTimeToStr(endDate, endTime);
            const timezoneStr = `UTC${Number(timezoneInput) >= 0 ? '+' : ''}${timezoneInput}:00`;
            return (
                <>
                    <span className='d-inline-block'>{start}</span> - <span className='d-inline-block'>{end}</span>&nbsp;
                    <span className='d-inline-block'>({timezoneStr})</span>
                </>
            );
        }
    },
    {
        key: 'title',
        header: 'Event Title',
        cell: (rowData: ICalendarEvent) => {
            const { eventInformation } = rowData;
            if (!eventInformation) {
                return null;
            }
            const { eventTitle } = eventInformation;
            const parsedTitle = eventTitle || '';

            if (rowData.isPublished) {
                return (
                    <a
                        href={filterType === FilterEventEnum.MyEvents ? rowData.url : UmbracoUtility.getCMSContentUrl(rowData.id)}
                        target='_blank'
                    >
                        {parsedTitle}
                    </a>
                );
            } else {
                return (
                    <>
                        <span className='d-inline-block'>{parsedTitle}</span>
                    </>
                );
            }            
        }
    },
    {
        key: 'state',
        header: 'State',
        widthPx: 80,
        cell: (rowData: ICalendarEvent) => <>{rowData.isPublished ? 'Published' : 'Unpublished'}</>
    },
    {
        key: 'author',
        header: 'Submitted By',
        cell: (rowData: ICalendarEvent) => {
            const { contactInformation } = rowData;
            if (!contactInformation) {
                return null;
            }
            const { fullName, emailAddress } = contactInformation;
            return (
                <>
                    {fullName}
                    {fullName && emailAddress ? ' ' : ''}
                    {emailAddress ? `(${emailAddress})` : ''}
                </>
            );
        }
    }
];

const tableConfig: ITableConfig<ICalendarEvent> = {
    getRowId: (rowData: ICalendarEvent) => rowData.id + ''
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100].map(pageSizeOption => ({
    label: pageSizeOption,
    value: pageSizeOption
}));

const CalendarEventListingApp = ({ calendarRootId }: { calendarRootId: number }) => {
    const {
        data: permission,
        isError: isGetPermissionError,
        error: getPermissionError
    } = calendarApi.useGetPermissionOnCalendarQuery(calendarRootId);
    const getPermissionErrorMessage = React.useMemo(() => {
        return ApiUtility.getFetchErrorMessage(error as FetchBaseQueryError);
    }, [getPermissionError]);

    const isPrivilegedMember = React.useMemo(() => {
        if (!permission) {
            return false;
        }
        const { isMemberLoggedIn, memberHasPrivilege } = permission;
        if (isMemberLoggedIn && memberHasPrivilege) {
            return true;
        }
        return false;
    }, [permission]);

    const isPrivilegedUser = React.useMemo(() => {
        if (!permission) {
            return false;
        }
        const { isUserLoggedIn, userHasPrivilege, isAdmin } = permission;
        if (isUserLoggedIn && (userHasPrivilege || isAdmin)) {
            return true;
        }
        return false;
    }, [permission]);

    const canSeeTable = React.useMemo(() => {
        if (isPrivilegedMember || isPrivilegedUser) {
            return true;
        }
        return false;
    }, [isPrivilegedMember, isPrivilegedUser]);

    const canHaveSpecialFilters = React.useMemo(() => {
        if (!permission) {
            return false;
        }
        const { isUserLoggedIn, isAdmin } = permission;
        if (isUserLoggedIn && isAdmin) {
            return true;
        }
        return false;
    }, [permission]);

    const FilterOptions = React.useMemo(() => {
        const options = [];
        if (isPrivilegedMember) {
            options.push({
                label: 'My events',
                value: FilterEventEnum.MyEvents
            });
        }
        if (canHaveSpecialFilters) {
            options.push(
                {
                    label: 'Unpublished events',
                    value: FilterEventEnum.AllUnpublishedEvents
                },
                {
                    label: 'Requested change events',
                    value: FilterEventEnum.AllRequestedChanges
                }
            );
        }
        return options;
    }, [isPrivilegedMember, canHaveSpecialFilters]);

    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(5);

    const [getEvents, { data, isSuccess, isError, error }] = calendarApi.useGetEventsMutation();
    const [filterType, setFilterType] = React.useState(null);

    React.useEffect(() => {
        if (!isPrivilegedMember && canHaveSpecialFilters) {
            setFilterType(FilterEventEnum.AllUnpublishedEvents);
        } else if (isPrivilegedMember) {
            setFilterType(FilterEventEnum.MyEvents);
        }
    }, [isPrivilegedMember, canHaveSpecialFilters]);

    React.useEffect(() => {
        if (filterType !== null) {
            getEvents({
                calendarNodeId: calendarRootId,
                filter: filterType,
                pageIndex: currentPage,
                pageSize
            });
        }
    }, [filterType, currentPage, pageSize]);

    const columnConfigs: Array<ITableColumn<ICalendarEvent>> = React.useMemo(() => {
        return getColumnConfig(filterType);
    }, [filterType]);

    const tableProps = React.useMemo(() => {
        return {
            columnConfigs,
            tableConfig,
            data: data || []
        };
    }, [columnConfigs, data]);

    return (
        <>
            {isGetPermissionError ? (
                <Alert type={AlertType.Error}>
                    <div>Something went wrong with get permission information!</div>
                    {getPermissionErrorMessage}
                </Alert>
            ) : (
                ''
            )}

            {canSeeTable ? (
                // Case can see table
                <>
                    <div className='d-flex align-items-center justify-content-between position-relative my-2'>
                        {FilterOptions.length > 1 ? (
                            <Select
                                placeholder='Filter By'
                                defaultValue={FilterOptions[0]}
                                options={FilterOptions}
                                onChange={option => {
                                    setFilterType(option.value);
                                }}
                                styles={{
                                    menu: provided => ({
                                        ...provided,
                                        width: 'auto',
                                        minWidth: 200
                                    }),
                                    control: provided => ({
                                        ...provided,
                                        width: 200
                                    })
                                }}
                            />
                        ) : (
                            ''
                        )}
                        <Select
                            placeholder='Page Size'
                            defaultValue={PAGE_SIZE_OPTIONS[0]}
                            options={PAGE_SIZE_OPTIONS}
                            onChange={option => {
                                setPageSize(option.value);
                            }}
                            styles={{
                                menu: provided => ({
                                    ...provided,
                                    width: 'auto',
                                    minWidth: 80
                                }),
                                control: provided => ({
                                    ...provided,
                                    width: 80
                                })
                            }}
                        />
                    </div>
                    <div className='position-relative'>
                        <Table {...tableProps} />
                        <Pagination
                            currentPage={currentPage}
                            totalCount={(data || []).length}
                            pageSize={pageSize}
                            onPageChange={page => setCurrentPage(page)}
                        />
                    </div>
                </>
            ) : (
                // Case cannot see table
                <div className='form-state-block d-flex align-items-center justify-content-center'>
                    <Spinner />
                </div>
            )}
        </>
    );
};

export class CalendarEventListing {
    constructor(element: HTMLElement) {
        const store = createStore();
        const calendarRootId = Number(element.dataset.calendarRootId);
        if (!calendarRootId || isNaN(calendarRootId)) {
            console.log(`invalid calendar root node id: ${calendarRootId}`);
            return;
        }
        ReactDOM.render(
            <Provider store={store}>
                <ErrorBoundary>
                    <CalendarEventListingApp {...{ calendarRootId }} />
                </ErrorBoundary>
            </Provider>,
            element
        );
    }
}
